import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
} from "@mui/material";
import { EditOutlined, WorkOutline } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import FieldLabel from "components/common/FieldLabel";
import FormFooter, {
  FOOTER_BUTTON_SX,
} from "components/userManagement/form/FormFooter";
import FormHeader from "components/userManagement/form/FormHeader";
import StepHeading from "components/userManagement/form/StepHeading";
import StepTabs, {
  type FormStep,
} from "components/userManagement/form/StepTabs";
import { pushNotification } from "features/notificationSlice";
import {
  type SecureClientOption,
  type SecureProject,
  useCreateSecureProjectMutation,
  useGetSecureClientsQuery,
  useGetSecureProjectCandidatesQuery,
  useUpdateSecureProjectMutation,
} from "features/secureUploads/secureUploads";
import { formatApiError } from "utils/formatApiError";

import ProjectAccessEditor from "./ProjectAccessEditor";
import {
  PROJECT_DESCRIPTION_MAX,
  PROJECT_NAME_MAX,
  detailsErrors,
} from "./projectModel";

type Step = "details" | "access";

type FieldErrors = Partial<
  Record<"client_id" | "name" | "description" | "member_ids", string>
>;

function fieldErrors(data: unknown): FieldErrors | null {
  if (!data || typeof data !== "object") return null;
  const found: FieldErrors = {};
  for (const key of [
    "client_id",
    "name",
    "description",
    "member_ids",
  ] as const) {
    const value = (data as Record<string, unknown>)[key];
    if (value)
      found[key] = Array.isArray(value) ? value.join(" ") : String(value);
  }
  return Object.keys(found).length ? found : null;
}

interface ProjectFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  /** The project to edit. */
  project?: SecureProject;
  /** Creating from a client's page: that client, fixed. */
  clientId?: number | null;
  onClose: () => void;
  onSaved?: (project: SecureProject) => void;
}

/** New Project (Details, then Access) and Edit Project (the details only). */
export default function ProjectFormDialog({
  open,
  mode,
  project,
  clientId: presetClientId = null,
  onClose,
  onSaved,
}: ProjectFormDialogProps) {
  const dispatch = useDispatch();
  const creating = mode === "create";
  const [step, setStep] = useState<Step>("details");
  const [clientId, setClientId] = useState<number | null>(presetClientId);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [members, setMembers] = useState<number[]>([]);
  const [touched, setTouched] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (!open) return;
    setStep("details");
    setClientId(creating ? presetClientId : (project?.client_id ?? null));
    setName(project?.name ?? "");
    setDescription(project?.description ?? "");
    setMembers([]);
    setTouched(false);
    setServerErrors({});
  }, [open, creating, presetClientId, project]);

  const { data: clients = [] } = useGetSecureClientsQuery(undefined, {
    skip: !open || !creating,
  });
  const { data: candidates = [], isFetching: loadingCandidates } =
    useGetSecureProjectCandidatesQuery(clientId ?? 0, {
      skip: !open || !creating || !clientId,
    });
  const [createProject, { isLoading: savingNew }] =
    useCreateSecureProjectMutation();
  const [updateProject, { isLoading: savingEdit }] =
    useUpdateSecureProjectMutation();
  const busy = savingNew || savingEdit;

  const local = detailsErrors({ clientId, name, description });
  const shown = (field: "client" | "name" | "description") =>
    touched ? local[field] : undefined;
  const detailsValid = Object.keys(local).length === 0;

  const steps: FormStep<Step>[] = [
    { key: "details", label: "Details" },
    { key: "access", label: "Access", disabled: !detailsValid },
  ];

  const toast = (message: string, severity: "success" | "error") =>
    dispatch(pushNotification({ message, severity }));

  const failed = (error: unknown, fallback: string) => {
    const data = (error as { data?: unknown })?.data;
    const fields = fieldErrors(data);
    if (fields) {
      setServerErrors(fields);
      if (fields.client_id || fields.name || fields.description)
        setStep("details");
      return;
    }
    toast(formatApiError(data, fallback), "error");
  };

  const save = async () => {
    setTouched(true);
    if (!detailsValid || !clientId) return;
    try {
      const saved = creating
        ? await createProject({
            client_id: clientId,
            name: name.trim(),
            description: description.trim(),
            member_ids: members,
          }).unwrap()
        : await updateProject({
            id: project!.id,
            name: name.trim(),
            description: description.trim(),
          }).unwrap();
      toast(creating ? "Project created." : "Project saved.", "success");
      onSaved?.(saved);
      onClose();
    } catch (error) {
      failed(
        error,
        creating
          ? "Could not create the project."
          : "Could not save the project.",
      );
    }
  };

  const clientOption = clients.find((client) => client.id === clientId) ?? null;

  const details = (
    <Box>
      <StepHeading
        title="Project Details"
        subtitle={
          creating
            ? "Provide the client, project name, and a brief description to get started."
            : "Update the project name and description."
        }
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: creating ? "1fr 1fr" : "1fr" },
          gap: 2,
          mt: 2,
        }}
      >
        {creating && (
          <Box>
            <FieldLabel required>Client</FieldLabel>
            <Autocomplete<SecureClientOption>
              options={clients}
              value={clientOption}
              disabled={presetClientId != null || busy}
              getOptionLabel={(client) => client.company_name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, client) => {
                setClientId(client?.id ?? null);
                setMembers([]);
                setServerErrors({});
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Select a client"
                  error={Boolean(shown("client") || serverErrors.client_id)}
                  helperText={shown("client") || serverErrors.client_id}
                />
              )}
            />
          </Box>
        )}
        <Box>
          <FieldLabel required>Project Name</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter a name for your project"
            value={name}
            disabled={busy}
            onChange={(e) => {
              setName(e.target.value);
              setServerErrors((prev) => ({ ...prev, name: undefined }));
            }}
            inputProps={{ maxLength: PROJECT_NAME_MAX }}
            error={Boolean(shown("name") || serverErrors.name)}
            helperText={
              shown("name") ||
              serverErrors.name ||
              `${name.trim().length}/${PROJECT_NAME_MAX}`
            }
          />
        </Box>
        <Box sx={{ gridColumn: "1 / -1" }}>
          <FieldLabel>Project Description</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter a short description of your project"
            value={description}
            disabled={busy}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: PROJECT_DESCRIPTION_MAX }}
            error={Boolean(shown("description") || serverErrors.description)}
            helperText={
              shown("description") ||
              serverErrors.description ||
              `${description.trim().length}/${PROJECT_DESCRIPTION_MAX}`
            }
          />
        </Box>
      </Box>
    </Box>
  );

  const access = (
    <Box>
      <StepHeading
        title="Project Access"
        subtitle="Choose which client users can view and collaborate on this project."
      />
      <Box sx={{ mt: 2 }}>
        {serverErrors.member_ids && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {serverErrors.member_ids}
          </Alert>
        )}
        <ProjectAccessEditor
          candidates={candidates}
          selected={members}
          onChange={setMembers}
          showConfidential
          loading={loadingCandidates}
          disabled={busy}
        />
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <FormHeader
          avatar={
            creating ? (
              <WorkOutline fontSize="small" />
            ) : (
              <EditOutlined fontSize="small" />
            )
          }
          title={creating ? "New Project" : "Edit Project"}
          onClose={onClose}
          closeDisabled={busy}
        />
        {creating && (
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
            <StepTabs<Step>
              steps={steps}
              value={step}
              onChange={(next) => {
                setTouched(true);
                if (next === "details" || detailsValid) setStep(next);
              }}
            />
          </Box>
        )}
        <Box sx={{ p: 3, overflowY: "auto" }}>
          {step === "details" ? details : access}
        </Box>
      </DialogContent>
      {creating ? (
        <FormFooter
          mode="create"
          isFirstStep={step === "details"}
          isLastStep={step === "access"}
          onBack={() => setStep("details")}
          onCancel={onClose}
          onContinue={() => {
            setTouched(true);
            if (detailsValid) setStep("access");
          }}
          onCreate={save}
          createDisabled={busy || !detailsValid}
          createLabel="Create Project"
          creating={savingNew}
        />
      ) : (
        <FormFooter
          mode="edit"
          isFirstStep
          onBack={() => undefined}
          onCancel={onClose}
        >
          <Button
            variant="contained"
            size="small"
            onClick={save}
            disabled={busy}
            sx={FOOTER_BUTTON_SX}
          >
            Save
          </Button>
        </FormFooter>
      )}
    </Dialog>
  );
}
